import {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { Observer } from 'gsap/dist/Observer'

import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'

gsap.registerPlugin(Observer)

import { cn } from '@/lib/utils'

const THUMB_ITEM_HEIGHT = 70

type ModalGalleryImage = {
  id: string
  src: string
  alt: string
  title?: string
  thumbSrc: string
  width?: number
  height?: number
  node: HTMLElement
}

const clampIndex = (index: number, length: number) => {
  if (!length) return 0
  return ((index % length) + length) % length
}

const getClosestImage = (node: HTMLElement) => {
  if (node instanceof HTMLImageElement) return node
  return node.querySelector('img')
}

const parseImageFromNode = (
  node: HTMLElement,
  index: number
): ModalGalleryImage | null => {
  const img = getClosestImage(node)
  const href = node instanceof HTMLAnchorElement ? node.href : ''
  const src =
    node.getAttribute('data-lightbox-src') ||
    img?.getAttribute('data-lightbox-src') ||
    img?.currentSrc ||
    img?.src ||
    href

  if (!src) return null

  const width = Number(
    node.getAttribute('data-lightbox-width') || img?.naturalWidth
  )
  const height = Number(
    node.getAttribute('data-lightbox-height') || img?.naturalHeight
  )

  return {
    id: node.getAttribute('data-lightbox-id') || `${index}-${src}`,
    src,
    alt:
      node.getAttribute('data-lightbox-alt') ||
      img?.alt ||
      node.getAttribute('aria-label') ||
      '',
    title: node.getAttribute('data-lightbox-title') || img?.title || undefined,
    thumbSrc:
      node.getAttribute('data-lightbox-thumb') ||
      img?.currentSrc ||
      img?.src ||
      src,
    width: Number.isFinite(width) && width > 0 ? width : undefined,
    height: Number.isFinite(height) && height > 0 ? height : undefined,
    node
  }
}

const collectImages = (clickedNode: HTMLElement) => {
  const nodes = Array.from(
    document.querySelectorAll<HTMLElement>('[data-lightbox]')
  )

  const images = nodes
    .map((node, index) => parseImageFromNode(node, index))
    .filter((image): image is ModalGalleryImage => image !== null)

  if (!images.length) {
    return { images, activeIndex: 0 }
  }

  const activeIndex = images.findIndex(
    ({ node }) =>
      node === clickedNode ||
      node.contains(clickedNode) ||
      clickedNode.contains(node)
  )

  return {
    images,
    activeIndex: activeIndex >= 0 ? activeIndex : 0
  }
}

const ModalGallery = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [images, setImages] = useState<ModalGalleryImage[]>([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [isMounted, setIsMounted] = useState(false)

  const rootRef = useRef<HTMLDivElement>(null)
  const arrowRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const mainImageRef = useRef<HTMLDivElement>(null)
  const counterRef = useRef<HTMLSpanElement>(null)
  const mobileCounterRef = useRef<HTMLDivElement>(null)
  const altRef = useRef<HTMLParagraphElement>(null)
  const isAnimatingRef = useRef(false)
  const hoverRef = useRef(false)
  const mouseRef = useRef({ x: 0, y: 0 })
  const swipeAreaRef = useRef<HTMLDivElement>(null)

  const imagesCount = images.length

  const activeImage = useMemo(() => {
    if (!imagesCount) return null
    return images[clampIndex(activeIndex, imagesCount)] ?? null
  }, [activeIndex, images, imagesCount])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const animateToIndex = useCallback(
    (newIndex: number) => {
      if (!imagesCount || isAnimatingRef.current || newIndex === activeIndex)
        return

      isAnimatingRef.current = true

      const timeline = gsap.timeline({
        onComplete: () => {
          isAnimatingRef.current = false
        }
      })

      timeline.to(mainImageRef.current, {
        opacity: 0,
        duration: 0.22,
        ease: 'power2.in'
      })

      timeline.to(
        [counterRef.current, altRef.current, mobileCounterRef.current].filter(
          Boolean
        ),
        {
          opacity: 0,
          duration: 0.22,
          ease: 'power2.in'
        },
        '<'
      )

      timeline.call(() => {
        setActiveIndex(clampIndex(newIndex, imagesCount))
      })

      timeline.fromTo(
        mainImageRef.current,
        { opacity: 0, scale: 1.08 },
        { opacity: 1, scale: 1, duration: 0.32, ease: 'power2.out' }
      )
    },
    [activeIndex, imagesCount]
  )

  const goNext = useCallback(() => {
    animateToIndex(activeIndex + 1)
  }, [activeIndex, animateToIndex])

  const goPrev = useCallback(() => {
    animateToIndex(activeIndex - 1)
  }, [activeIndex, animateToIndex])

  const goNextRef = useRef(goNext)
  goNextRef.current = goNext
  const goPrevRef = useRef(goPrev)
  goPrevRef.current = goPrev
  const closeRef = useRef(close)
  closeRef.current = close

  // Swipe left/right/down via GSAP Observer (solo mobile)
  useGSAP(
    () => {
      if (!rootRef.current || !isOpen || !imagesCount) return
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches)
        return

      const swipeObs = Observer.create({
        target: rootRef.current,
        type: 'touch',
        onLeft: () => goNextRef.current(),
        onRight: () => goPrevRef.current(),
        tolerance: 50,
        preventDefault: true
      })

      const closeObs = Observer.create({
        target: rootRef.current,
        type: 'touch',
        onDown: () => closeRef.current(),
        tolerance: 200,
        preventDefault: true
      })

      return () => {
        swipeObs.kill()
        closeObs.kill()
      }
    },
    { dependencies: [isOpen, imagesCount] }
  )

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!isMounted) return

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      const trigger = target?.closest<HTMLElement>('[data-lightbox]')
      if (!trigger) return

      const { images: parsedImages, activeIndex: parsedActiveIndex } =
        collectImages(trigger)

      if (!parsedImages.length) return

      event.preventDefault()

      setImages(parsedImages)
      setActiveIndex(parsedActiveIndex)
      setIsOpen(true)
    }

    document.addEventListener('click', onClick, true)
    return () => {
      document.removeEventListener('click', onClick, true)
    }
  }, [isMounted])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    gsap.to(root, {
      opacity: isOpen ? 1 : 0,
      pointerEvents: isOpen ? 'auto' : 'none',
      duration: 0.25,
      ease: 'power2.out'
    })

    if (isOpen && mainImageRef.current) {
      gsap.fromTo(
        mainImageRef.current,
        { opacity: 0, scale: 1.1 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.55,
          ease: 'power3.out',
          delay: 0.08
        }
      )
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const prevBodyOverflow = document.body.style.overflow
    const prevHtmlOverflow = document.documentElement.style.overflow

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = prevBodyOverflow
      document.documentElement.style.overflow = prevHtmlOverflow
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      }

      if (event.key === 'ArrowRight') {
        goNext()
      }

      if (event.key === 'ArrowLeft') {
        goPrev()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [close, goNext, goPrev, isOpen])

  useEffect(() => {
    if (!isOpen || !imagesCount || !listRef.current) return

    listRef.current.scrollTo({
      top: clampIndex(activeIndex, imagesCount) * THUMB_ITEM_HEIGHT,
      left: 0,
      behavior: 'smooth'
    })
  }, [activeIndex, imagesCount, isOpen])

  useEffect(() => {
    if (!isOpen || !counterRef.current) return

    gsap.fromTo(
      [counterRef.current, altRef.current, mobileCounterRef.current].filter(
        Boolean
      ),
      { yPercent: 100, opacity: 0 },
      {
        yPercent: 0,
        opacity: 1,
        duration: 0.5,
        ease: 'power3.out'
      }
    )
  }, [activeIndex, isOpen])

  useEffect(() => {
    if (!isOpen || !arrowRef.current) return
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return

    let rafId = 0

    const onMove = (event: MouseEvent) => {
      mouseRef.current = { x: event.clientX, y: event.clientY }
    }

    const update = () => {
      if (arrowRef.current) {
        gsap.to(arrowRef.current, {
          top: mouseRef.current.y,
          left: mouseRef.current.x,
          opacity: hoverRef.current ? 1 : 0,
          rotation: mouseRef.current.x > window.innerWidth / 2 ? 0 : 180,
          transformOrigin: 'center',
          duration: 0.35,
          ease: 'power3.out'
        })
      }

      rafId = requestAnimationFrame(update)
    }

    document.addEventListener('mousemove', onMove)
    rafId = requestAnimationFrame(update)

    return () => {
      document.removeEventListener('mousemove', onMove)
      cancelAnimationFrame(rafId)
    }
  }, [isOpen])

  const handleMainClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (imagesCount <= 1) return
      // Su mobile lo swipe gestisce la navigazione
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches)
        return

      const bounds = event.currentTarget.getBoundingClientRect()
      const isRightSide = event.clientX > bounds.left + bounds.width / 2

      if (isRightSide) {
        goNext()
      } else {
        goPrev()
      }
    },
    [goNext, goPrev, imagesCount]
  )

  if (!isMounted) return null

  return createPortal(
    <div
      ref={rootRef}
      className={cn(
        'pointer-events-none fixed inset-0 z-[110] h-[100lvh] bg-[black] p-spacing-sm opacity-0 md:p-spacing-xl',
        'flex gap-spacing-lg'
      )}
      role="dialog"
      aria-modal="true"
      aria-hidden={!isOpen}
    >
      <div className="pointer-events-none absolute left-0 top-0 z-40 h-[54px] w-full bg-gradient-to-b from-[black] to-transparent md:h-[104px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 z-40 h-[54px] w-full bg-gradient-to-t from-[black] to-transparent md:h-[104px]" />

      <div
        ref={arrowRef}
        className="pointer-events-none absolute z-20 ml-[-14px] mt-[-35px] hidden size-[32px] origin-center text-[white] lg:block"
      >
        <SVGIcon symbol="arrow-next" className="size-[32px]" />
      </div>

      <div
        ref={listRef}
        data-lenis-prevent
        className="scrollbar relative z-10 mr-[-20px] hidden h-full w-[96px] overflow-auto bg-[black] pr-[20px] md:block"
      >
        <div className="flex flex-col gap-[10px]">
          {images.map((image, index) => {
            const active = clampIndex(activeIndex, imagesCount) === index

            return (
              <button
                key={image.id}
                type="button"
                onClick={() => animateToIndex(index)}
                className={cn(
                  'w-full cursor-pointer overflow-hidden opacity-50 transition-opacity duration-200',
                  { 'border border-[white] opacity-100': active }
                )}
                aria-label={`Open image ${index + 1}`}
              >
                <img
                  src={image.thumbSrc}
                  alt={image.alt}
                  className="block aspect-[3/4] h-auto w-full object-cover"
                />
              </button>
            )
          })}
        </div>
      </div>

      <div
        ref={swipeAreaRef}
        className="relative h-full flex-[1_1_auto]"
        onClick={handleMainClick}
        onMouseEnter={() => {
          hoverRef.current = true
        }}
        onMouseLeave={() => {
          hoverRef.current = false
        }}
        role="presentation"
      >
        <div
          ref={mainImageRef}
          className="will-change-opacity absolute inset-0 will-change-transform"
        >
          {activeImage ? (
            <figure
              key={`main-${activeImage.id}-${activeIndex}`}
              className="absolute inset-0 flex h-full w-full items-center justify-center"
            >
              <img
                src={activeImage.src}
                alt={activeImage.alt}
                title={activeImage.title}
                width={activeImage.width}
                height={activeImage.height}
                className="pointer-events-none absolute inset-0 h-full w-full object-contain"
              />
            </figure>
          ) : null}
        </div>
      </div>

      <button
        type="button"
        aria-label="Close gallery"
        onClick={close}
        className={cn(
          'flex size-[60px] items-center justify-center rounded-full border border-border-secondary-dark/20 bg-[black]/20 text-[white] backdrop-blur-[17px]',
          'absolute right-spacing-sm top-spacing-sm z-20 z-30 md:right-spacing-xl md:top-spacing-xl'
        )}
      >
        <SVGIcon symbol="close" className="size-[16px]" />
      </button>

      <div className="hidden flex-col items-end justify-end md:flex">
        <div
          className="pointer-events-none z-20 hidden items-baseline overflow-hidden whitespace-nowrap pb-[1px] text-[white] md:flex"
          data-modal-gallery-counter
        >
          <Typography
            ref={counterRef}
            variant="h1"
            component="span"
            className="inline-block will-change-transform"
          >
            {(activeIndex + 1).toString().padStart(2, '0')}
          </Typography>
          <Typography
            variant="regular"
            component="span"
            className="inline-block translate-y-[2px]"
          >
            &nbsp;/&nbsp;{imagesCount.toString().padStart(2, '0')}
          </Typography>
        </div>
      </div>

      <div
        ref={mobileCounterRef}
        className="absolute bottom-spacing-sm left-0 z-20 flex w-[100vw] !translate-x-0 items-center justify-between gap-[16px] overflow-hidden whitespace-nowrap px-spacing-sm text-[white] md:hidden"
      >
        <button
          type="button"
          aria-label="Immagine precedente"
          onClick={goPrev}
          className="flex size-[32px] items-center justify-center rounded-full border border-[white]/40"
        >
          <SVGIcon symbol="arrow-prev" className="size-[16px]" />
        </button>
        <Typography variant="regular" component="span">
          {(activeIndex + 1).toString().padStart(2, '0')}
          &nbsp;/&nbsp;{imagesCount.toString().padStart(2, '0')}
        </Typography>
        <button
          type="button"
          aria-label="Immagine successiva"
          onClick={goNext}
          className="flex size-[32px] items-center justify-center rounded-full border border-[white]/40"
        >
          <SVGIcon symbol="arrow-next" className="size-[16px]" />
        </button>
      </div>

      <div
        className={cn(
          'absolute z-20 w-full',
          'hidden',
          'md:bottom-spacing-xl md:left-[174px] md:block md:w-[calc(100%-382px)]'
        )}
      >
        {activeImage?.alt && (
          <Typography
            variant="regular"
            component="p"
            className="text-text-primary-dark"
            ref={altRef}
          >
            {activeImage.alt}
          </Typography>
        )}
      </div>
    </div>,
    document.body
  )
}

export default ModalGallery
